import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Check, X, Loader2, Package, History } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { formatApiError } from '@/utils/apiError';

const api = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api/staff/sales-suppliers${path}`, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });
  const d = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(formatApiError(d) || 'Request failed');
  return d;
};

export async function fetchSuppliers() {
  return api('');
}

/** Manage the list of companies you spend money with (plus the built-in Stock entry). */
export function SupplierManager({ open, onClose, onChanged }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState(null); // { id, name }
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await fetchSuppliers();
      setList(d.suppliers || []);
      if (d.seeded_from_history > 0) toast.info(`Imported ${d.seeded_from_history} supplier${d.seeded_from_history === 1 ? '' : 's'} from your past orders — check the names and rename any abbreviations.`);
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const done = async () => { await load(); onChanged?.(); };

  const add = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try { await api('', { method: 'POST', body: JSON.stringify({ name: newName }) }); setNewName(''); toast.success('Supplier added'); await done(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const rename = async () => {
    if (!editing?.name.trim()) return;
    setBusy(true);
    try {
      const d = await api(`/${editing.id}`, { method: 'PUT', body: JSON.stringify({ name: editing.name }) });
      toast.success(d.orders_updated ? `Renamed · ${d.orders_updated} past order${d.orders_updated === 1 ? '' : 's'} updated` : 'Renamed');
      setEditing(null); await done();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.name}" from the list? Past orders keep the name.`)) return;
    setBusy(true);
    try { await api(`/${s.id}`, { method: 'DELETE' }); toast.success('Supplier removed'); await done(); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="supplier-manager">
        <DialogHeader>
          <DialogTitle>Suppliers</DialogTitle>
          <DialogDescription>The companies you spend money with. Pick one on every cost line when logging an order.</DialogDescription>
        </DialogHeader>

        <div className="flex gap-2">
          <Input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="Add a company, e.g. Derbyshire Aggregates" className="bg-white" data-testid="supplier-new-name" />
          <Button onClick={add} disabled={busy || !newName.trim()} className="bg-[#1f4030] hover:bg-[#16302a] text-white" data-testid="supplier-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>

        {loading ? <div className="py-8 text-center text-gray-500 text-sm">Loading…</div> : (
          <ul className="divide-y border rounded-lg bg-white" data-testid="supplier-list">
            {list.map((s) => (
              <li key={s.id} className="flex items-center gap-2 px-3 py-2" data-testid={`supplier-row-${s.id}`}>
                {s.is_stock ? <Package className="w-4 h-4 text-emerald-700 shrink-0" /> : <span className="w-4" />}
                {editing?.id === s.id ? (
                  <>
                    <Input autoFocus value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} onKeyDown={(e) => { if (e.key === 'Enter') rename(); if (e.key === 'Escape') setEditing(null); }} className="h-8 bg-white" data-testid="supplier-rename-input" />
                    <button type="button" onClick={rename} disabled={busy} className="p-1.5 text-emerald-700 hover:bg-emerald-50 rounded" aria-label="Save" data-testid="supplier-rename-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}</button>
                    <button type="button" onClick={() => setEditing(null)} className="p-1.5 text-gray-400 hover:bg-gray-100 rounded" aria-label="Cancel"><X className="w-4 h-4" /></button>
                  </>
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">{s.name}</div>
                      <div className="text-[11px] text-gray-500 flex items-center gap-2">
                        {s.is_stock ? 'Built in — takes products out of the warehouse' : `${s.uses} cost line${s.uses === 1 ? '' : 's'}`}
                        {s.seeded_from_history && <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5" data-testid={`supplier-imported-${s.id}`}><History className="w-3 h-3" /> imported — check name</span>}
                      </div>
                    </div>
                    {!s.is_stock && (
                      <>
                        <button type="button" onClick={() => setEditing({ id: s.id, name: s.name })} className="p-1.5 text-gray-400 hover:text-gray-900" aria-label="Rename" data-testid={`supplier-rename-${s.id}`}><Pencil className="w-4 h-4" /></button>
                        <button type="button" onClick={() => remove(s)} className="p-1.5 text-gray-400 hover:text-rose-600" aria-label="Remove" data-testid={`supplier-delete-${s.id}`}><Trash2 className="w-4 h-4" /></button>
                      </>
                    )}
                  </>
                )}
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
